import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { contractsApi, type Contract, type ContractRedUgovora } from "../api/contracts";
import { CONTRACT_STATUS_KEY } from "../pages/ContractsPage";
import { formatDate, formatMoney, formatNumber, intlLocale } from "../lib/format";

/**
 * Ugovori jedne firme, kao kartice a ne kao red teksta.
 *
 * Red koji glasi „2600235, 2600257 Biopro 10 · 26. 1. 2026." ne kaže ništa: ni koliko je
 * ugovoreno, ni koliko je preuzeto, ni koliko je vremena ostalo. A to su tri pitanja zbog kojih
 * se ugovor kupca i otvara.
 *
 * Kartica zato nosi robu sa količinom i cenom, ispunjenost prema proteklom roku, preostalo i
 * datum isteka sa brojem dana. Uslovi (paritet, rok plaćanja) idu na dno, jer se gledaju ređe.
 */
export function CompanyContracts({ ugovori }: { ugovori: Contract[] }) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const [brojevi, setBrojevi] = useState<Map<string, ContractRedUgovora>>(new Map());

  // Ispunjenost se računa na jednom mestu, u pregledu ugovora, pa se ovde samo čita: dva računa
  // iste stvari pre ili kasnije daju dva različita broja.
  useEffect(() => {
    let otkazano = false;
    contractsApi.overview(new Date().getFullYear())
      .then((p) => { if (!otkazano) setBrojevi(new Map(p.poUgovoru.map((r) => [r.ugovorId, r]))); })
      .catch(() => undefined);
    return () => { otkazano = true; };
  }, []);

  // Najpre oni koji još traju, pa po najbližem roku: ugovor kome ističe rok je onaj o kom treba
  // nešto odlučiti.
  const poredjani = useMemo(() => [...ugovori].sort((a, b) => {
    const da = brojevi.get(a.id)?.danaDoIsteka;
    const db = brojevi.get(b.id)?.danaDoIsteka;
    const ka = da == null ? 1 : da < 0 ? 2 : 0;
    const kb = db == null ? 1 : db < 0 ? 2 : 0;
    if (ka !== kb) return ka - kb;
    return (da ?? 0) - (db ?? 0);
  }), [ugovori, brojevi]);

  if (ugovori.length === 0) return null;

  return (
    <div style={spisak}>
      {poredjani.map((c) => (
        <Kartica key={c.id} ugovor={c} red={brojevi.get(c.id)} locale={locale} t={t} />
      ))}
    </div>
  );
}

function Kartica({
  ugovor, red, locale, t,
}: {
  ugovor: Contract;
  red?: ContractRedUgovora;
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  const valuta = ugovor.currency ?? "EUR";
  const kolicina = (n: number | null | undefined) =>
    n == null ? "—" : formatNumber(n, locale, { maximumFractionDigits: 0 });

  const dana = red?.danaDoIsteka;
  const isteklo = dana != null && dana < 0;
  const uskoro = dana != null && dana >= 0 && dana <= 30;

  // Ispunjenost se boji prema proteklom roku, ne prema fiksnom pragu: 40% u januaru je dobro, a
  // u decembru nije.
  // Contracts for the same goods share what was invoiced, the older one first, so the figure is
  // this contract's share and a note says so.
  const deljeno = (red?.deliSa ?? 1) > 1;
  const pct = red?.realizacijaPct ?? null;
  const proteklo = red?.protekloPct ?? null;
  const boja = pct == null || proteklo == null ? "var(--ink-4)"
    : pct - proteklo < -5 ? "var(--err)"
    : pct - proteklo > 5 ? "var(--ok)" : "var(--warn)";

  const uslovi = [ugovor.incoterm, ugovor.incotermPlace, ugovor.paymentTerms]
    .filter((x): x is string => Boolean(x));

  return (
    <article style={kartica}>
      <header style={zaglavlje}>
        <span style={{ minWidth: 0 }}>
          <b style={{ fontSize: 13.5, color: "var(--ink)" }}>{ugovor.referenceNumber}</b>
          <span style={naslov}>{ugovor.title}</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={stanje}>
            {t(CONTRACT_STATUS_KEY[ugovor.status] ?? "contracts.status.unknown", ugovor.status)}
          </span>
          {ugovor.expiresAt && (
            <span style={{
              ...rok,
              color: isteklo ? "var(--ink-4)" : uskoro ? "var(--warn)" : "var(--ink-3)",
              borderColor: uskoro && !isteklo ? "var(--warn)" : "var(--border)",
            }}>
              {t("company.contracts.until", "do {{datum}}", { datum: formatDate(ugovor.expiresAt) })}
              {dana != null && (
                <b style={{ marginLeft: 6 }}>
                  {isteklo
                    ? t("company.contracts.expired", "isteklo")
                    : t("company.contracts.daysLeft", "{{count}} dana", { count: dana })}
                </b>
              )}
            </span>
          )}
        </span>
      </header>

      {ugovor.lines.length > 0 && (
        <ul style={stavke}>
          {ugovor.lines.map((l) => (
            <li key={l.id} style={stavka}>
              <span style={imeArtikla} title={l.productName ?? undefined}>{l.productName ?? "—"}</span>
              <span style={brojevi}>
                {/* Okvirni ugovor vezuje cenu a ne obim: količine nema i ne piše se nula. */}
                {l.quantity != null && (
                  <span>{kolicina(l.quantity)}{l.unit ? ` ${l.unit}` : ""}</span>
                )}
                {l.unitPrice != null && (
                  <b>{formatMoney(l.unitPrice, valuta, locale, { maximumFractionDigits: 2 })}
                    {l.unit ? `/${l.unit}` : ""}</b>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {red && (
        <div style={ispunjenost}>
          <span style={{ ...tiho, flexShrink: 0 }}>
            {t("company.contracts.fulfilment", "Ispunjenost")}
          </span>
          <span style={traka}>
            <span style={{
              ...trakaPuna, background: boja,
              width: `${Math.min(100, Math.max(0, pct ?? 0))}%`,
            }} />
          </span>
          <b style={{ color: boja, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
            {pct == null ? "—" : `${formatNumber(pct, locale, { maximumFractionDigits: 0 })}%`}
          </b>
          {red.ugovorenaKolicina != null && red.isporucenaKolicina != null && (
            <span style={{ ...tiho, flexShrink: 0 }}>
              {t("company.contracts.remaining", "preostalo {{kg}}", {
                kg: kolicina(Math.max(0, red.ugovorenaKolicina - red.isporucenaKolicina)),
              })}
            </span>
          )}
          {/* The count includes this contract, the same as on the contracts overview. */}
          {deljeno && (
            <span style={{ ...tiho, flexBasis: "100%" }}>
              {t("company.contracts.shared",
                 "Istu robu pokrivaju {{count}} ugovora: fakturisano ide redom, stariji ugovor se puni prvi.",
                 { count: red.deliSa })}
            </span>
          )}
        </div>
      )}

      {uslovi.length > 0 && (
        <div style={uslovRed}>
          {uslovi.map((u) => <span key={u} style={znak}>{u}</span>)}
        </div>
      )}
    </article>
  );
}

const spisak: CSSProperties = { display: "flex", flexDirection: "column", gap: 10 };
const stanje: CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", border: "1px solid var(--border)",
  borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
};
const kartica: CSSProperties = {
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", padding: "10px 12px",
  display: "flex", flexDirection: "column", gap: 8,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
};
const naslov: CSSProperties = { color: "var(--ink-3)", marginLeft: 10, fontSize: 12.5 };
const rok: CSSProperties = {
  fontSize: 11.5, border: "1px solid var(--border)", borderRadius: 999, padding: "2px 9px",
  whiteSpace: "nowrap",
};
const stavke: CSSProperties = {
  listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 3,
};
const stavka: CSSProperties = {
  display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, fontSize: 12.5,
};
const imeArtikla: CSSProperties = {
  minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  color: "var(--ink-2)",
};
const brojevi: CSSProperties = {
  display: "flex", gap: 12, flexShrink: 0, fontVariantNumeric: "tabular-nums", color: "var(--ink)",
};
const ispunjenost: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, fontSize: 12, flexWrap: "wrap",
};
const traka: CSSProperties = {
  flex: 1, minWidth: 90, height: 6, background: "var(--hover)", borderRadius: 999,
  overflow: "hidden", display: "block",
};
const trakaPuna: CSSProperties = { height: "100%", borderRadius: 999, display: "block" };
const tiho: CSSProperties = { color: "var(--ink-3)" };
const uslovRed: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6 };
const znak: CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", border: "1px solid var(--border)",
  borderRadius: 999, padding: "1px 8px",
};
